import React, { useContext, useMemo } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useLeaveTypes from '@ubeya/shared/hooks/account/useLeaveTypes';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import { API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { Flex, Tooltip } from '@ubeya/shared-web/components';
import { ReactComponent as BaseErrorIcon } from '@ubeya/shared-web/assets/error.svg';
import { RULE_PERIOD_MAP_BY_SLUG } from '@ubeya/shared-web/constants';
import { formatPreferenceTimeRange, getEmployeeAgeWarning } from 'scheduling/utils';
import { SLOT_WARNING_TYPES } from 'scheduling/constants';
import { formatLeaveWarningLabel } from 'scheduling/leaveIndications';

const StatusDot = styled.div`
  border-radius: 100%;
  width: 10px;
  height: 10px;
  margin-right: 5px;
  background-color: ${({ theme }) => theme.colors.redNegative};
`;

const Wrapper = styled.div``;

const Title = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const BookingWarningWrapper = styled(Flex)``;

const BookingWarningBoldItem = styled(Flex)`
  font-weight: 500;
`;

const ErrorIcon = styled(BaseErrorIcon).attrs(() => ({ className: 'booking-warnings-error-icon' }))`
  position: absolute;
  right: 0;
  top: 12px;
  width: 17px;
  height: 17px;
`;

const WarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-left: 10px;
  gap: 4px;
`;

const EmployeeBookingWarningsRenderer = ({
  // { type, format } are the properties of external warnings
  externalFormattedWarnings = [],
  slotWarnings = [],
  projectId,
  shiftId,
  branchId,
  age,
  user,
  // when it's availability context, we do not have shift id since the employee is not booked yet
  isAvailabilityContext = false
}) => {
  const { mappedBranches } = useBranches();
  const { leaveTypes } = useLeaveTypes();
  const { mappedPtoTypes } = usePtoTypes();
  const { t, timeFormat, dateFormat, isBlockDemographicFiltersInSchedulingEnabled } = useContext(ConfigContext);

  const mappedLeaveTypes = useMemo(
    () => (leaveTypes || []).reduce((all, leaveType) => ({ ...all, [leaveType.id]: leaveType }), {}),
    [leaveTypes]
  );
  const { isUnder18, isWarning } = getEmployeeAgeWarning({
    age,
    isAgeHidden: isBlockDemographicFiltersInSchedulingEnabled
  });

  const renderBookingWarnings = useMemo(() => {
    const renderBookingWarningsArr = [];
    const externalWarningsTypesSet = new Set();

    // first add external warnings
    (externalFormattedWarnings || []).forEach(({ type, format }) => {
      externalWarningsTypesSet.add(type);
      renderBookingWarningsArr.push(format);
    });

    // then add slot warnings that are NOT already included in external warnings
    (slotWarnings || [])
      .filter(({ type }) => !externalWarningsTypesSet.has(type))
      .forEach(({ type, slug, metadata = {} }) => {
        switch (type) {
          case SLOT_WARNING_TYPES.OVERLAPPING_SHIFTS.type:
          case SLOT_WARNING_TYPES.SHORT_REST_TIME.type:
          case SLOT_WARNING_TYPES.BOOKED_SAME_DAY.type: {
            // if it's isAvailabilityContext is the shift id should be null, otherwise we should check if the metadata shiftId matches the current shiftId
            if (!isAvailabilityContext && metadata?.shiftId !== shiftId) {
              return;
            }

            if (!metadata.conflictingShiftDetails) {
              return;
            }

            const { conflictingShiftDetails } = metadata;

            const {
              shiftLocationName,
              branchId: overlappingBranchId,
              projectId: overlappingProjectId,
              startTime,
              endTime,
              isConfirmed
            } = conflictingShiftDetails;

            const branchName =
              overlappingBranchId !== branchId ? ` (${t('branch')}: ${mappedBranches[overlappingBranchId]?.name})` : '';

            const locationName = overlappingProjectId !== projectId || shiftLocationName ? shiftLocationName : '';

            const overlappingShiftTime = `${moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat)}${
              endTime ? `- ${moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)}` : ''
            }`;

            const accountSettingRestHours = metadata?.warningTypeData?.requiredRestHours ?? 8;
            renderBookingWarningsArr.push(
              // for the spacing inside the translation
              <BookingWarningWrapper style={{ whiteSpace: 'pre-wrap' }}>
                <Trans
                  i18nKey={`booking_warnings_${slug}`}
                  values={{ locationName, accountSettingRestHours }}
                  components={{ strong: <strong /> }}
                />
                {`${locationName ? ', ' : ''}${overlappingShiftTime}${branchName}`}
                {isConfirmed !== undefined && (
                  <BookingWarningBoldItem>
                    {`, ${t(isConfirmed ? 'confirmed' : 'notConfirmed')}`}
                  </BookingWarningBoldItem>
                )}
              </BookingWarningWrapper>
            );
            break;
          }

          case SLOT_WARNING_TYPES.DAILY_WORKING_HOURS_LIMIT.type: {
            renderBookingWarningsArr.push(
              <Trans
                i18nKey="booking_warnings_daily_working_hours_limit"
                values={metadata}
                components={{ strong: <strong /> }}
              />
            );
            break;
          }

          case SLOT_WARNING_TYPES.COMPLIANCE.type: {
            const { isEmployeeCompliant = true, compliantExpDate } = metadata || {};
            if (isEmployeeCompliant) {
              return;
            }

            renderBookingWarningsArr.push(
              `${t('notCompliant')} ${
                compliantExpDate ? `${t('compliantSince')} ${moment(compliantExpDate).format(dateFormat)}` : ''
              }`
            );

            break;
          }

          case SLOT_WARNING_TYPES.WTD.type: {
            const { employeesNotCompliantToRules = [], isEmployeeWTDCompliant = true, isCurrentIssue } = metadata || {};

            if (isEmployeeWTDCompliant) {
              return;
            }

            employeesNotCompliantToRules.forEach(({ rules, name }) =>
              rules.forEach((rule) => {
                renderBookingWarningsArr.push(
                  <div>
                    <Trans
                      i18nKey={
                        isCurrentIssue ? 'employeeExceedWorkingHoursBody' : 'employeeExceedWorkingHoursFutureBody'
                      }
                      values={{
                        period: rule.period === RULE_PERIOD_MAP_BY_SLUG.WEEKLY ? t('weeklySmall') : rule.period,
                        hours: rule.hours,
                        name,
                        employeeName: [user?.firstName, user?.lastName]?.filter((val) => !!val)?.join(' ')
                      }}
                      components={{ bold: <strong /> }}
                    />
                  </div>
                );
              })
            );
            break;
          }

          case SLOT_WARNING_TYPES.PARTIAL_PREFERRED.type: {
            const preferenceTimeRange = formatPreferenceTimeRange({ metadata, timeFormat });

            renderBookingWarningsArr.push(
              preferenceTimeRange ? t('prefer_to_be_in_shift_between', preferenceTimeRange) : t('prefer_to_be_in_shift')
            );

            break;
          }

          case SLOT_WARNING_TYPES.RELEASE_REQUEST.type: {
            const warningMessage = `${t(slug)}: ${metadata?.employeeReason || ''} ${
              metadata?.employeeReasonOther ? ' - ' + metadata?.employeeReasonOther || '' : ''
            }`;
            renderBookingWarningsArr.push(warningMessage);

            break;
          }

          case SLOT_WARNING_TYPES.UNDER_AGE.type:
          case SLOT_WARNING_TYPES.OVER_AGE.type: {
            if (isBlockDemographicFiltersInSchedulingEnabled) {
              return;
            }

            renderBookingWarningsArr.push(t(slug));
            break;
          }

          // day off / pto / leave record / leave request all carry the leave type and whether it is
          // still pending - same label the availability side renders, instead of a bare "Day off"
          case SLOT_WARNING_TYPES.DAY_OFF.type:
          case SLOT_WARNING_TYPES.PTO.type:
          case SLOT_WARNING_TYPES.LEAVE_RECORD.type:
          case SLOT_WARNING_TYPES.LEAVE_REQUEST.type: {
            renderBookingWarningsArr.push(
              formatLeaveWarningLabel({ warning: { type, slug, metadata }, mappedLeaveTypes, mappedPtoTypes, t })
            );
            break;
          }

          case SLOT_WARNING_TYPES.DN_SPECIFIC_EMIRATES.type:
          case SLOT_WARNING_TYPES.DN_VISA_EXPIRY.type:
          case SLOT_WARNING_TYPES.DN_DBS_EXPIRY.type:
          case SLOT_WARNING_TYPES.DN_EVENT_ACCREDITATION.type:
          case SLOT_WARNING_TYPES.CHESTER_RIGHT_TO_WORK_EXPIRY.type:
          case SLOT_WARNING_TYPES.LEGENDS_CHELSEA_VISA_EXPIRY.type:
          case SLOT_WARNING_TYPES.CONTRACT_WORKING_LIMIT.type:
          case SLOT_WARNING_TYPES.PENDING_EMPLOYEE.type: {
            renderBookingWarningsArr.push(t(slug));
            break;
          }

          default: {
            renderBookingWarningsArr.push(t(slug));
            break;
          }
        }
      });

    // AGE WARNING
    if (age && !isBlockDemographicFiltersInSchedulingEnabled) {
      if (isUnder18) {
        renderBookingWarningsArr.push(t('underEighteenRiskAssmntTooltip'));
      }

      if (isWarning) {
        renderBookingWarningsArr.push(t('seniorRiskAssmntTooltip'));
      }
    }

    return renderBookingWarningsArr;
  }, [
    age,
    branchId,
    externalFormattedWarnings,
    isBlockDemographicFiltersInSchedulingEnabled,
    isAvailabilityContext,
    isUnder18,
    isWarning,
    mappedBranches,
    mappedLeaveTypes,
    mappedPtoTypes,
    projectId,
    shiftId,
    slotWarnings,
    dateFormat,
    t,
    timeFormat,
    user?.firstName,
    user?.lastName
  ]);

  if (renderBookingWarnings.length === 0) {
    return <></>;
  }

  return (
    <Tooltip
      trigger={isMobileOnly ? 'click' : undefined}
      body={
        <Wrapper>
          <Title>
            <StatusDot />
            {renderBookingWarnings.length === 1
              ? t('thereIsAnIssue')
              : t('thereAreIssues', { warningsNumber: renderBookingWarnings.length })}
            :
          </Title>
          {renderBookingWarnings.map((warning, index) => (
            <WarningItem key={index}>
              {index + 1}. {warning}
            </WarningItem>
          ))}
        </Wrapper>
      }>
      <ErrorIcon />
    </Tooltip>
  );
};

export default EmployeeBookingWarningsRenderer;
